import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-border bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-foreground text-background hover:bg-foreground/90 shadow-elegant hover:shadow-glow",
        glass: "backdrop-blur-sm bg-[var(--glass)] border border-[var(--glass-border)] hover:bg-accent/50 hover:border-border/50",
        "outline-glow": "border border-primary/30 bg-background/5 text-foreground hover:border-primary/60 hover:bg-primary/5 hover:shadow-glow",
        gradient: "bg-[var(--gradient-primary)] text-primary-foreground hover:opacity-90 shadow-elegant hover:shadow-glow",
        "cta-primary": "bg-gradient-to-r from-primary to-primary-glow hover:from-primary-glow hover:to-primary text-white font-semibold shadow-lg hover:shadow-primary/20 border-0 hover:scale-[1.02] transition-all duration-300",
        "cta-secondary": "bg-transparent hover:bg-primary/10 text-white border-2 border-primary/40 hover:border-primary/60 font-medium backdrop-blur-sm transition-all duration-300",
        "cta-header-primary": "bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-full px-6 py-3 shadow-lg hover:shadow-primary/20 border-0 transition-all duration-300",
        "cta-header-secondary": "bg-transparent hover:bg-primary/10 text-foreground border-2 border-border hover:border-primary/60 font-medium rounded-full px-6 py-3 backdrop-blur-sm transition-all duration-300 group",
        "cta-outline": "bg-transparent hover:bg-primary/10 text-foreground border-2 border-border hover:border-primary/60 font-medium rounded-full px-6 py-3 backdrop-blur-sm transition-all duration-300"
      },
      size: {
        default: "h-10 px-6 py-2",
        sm: "h-9 rounded-md px-4",
        lg: "h-12 rounded-lg px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg font-semibold",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
